import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Search, Loader2, User, Wallet, Plus, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CustomerBalanceModal = ({ open, onClose, onAddToCart }) => {
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingBalance, setFetchingBalance] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");

  useEffect(() => {
    if (open) {
      fetchCustomers();
    } else {
      // Reset state when closing
      setSelectedCustomer(null);
      setSearchQuery("");
      setPaymentAmount("");
    }
  }, [open]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers`, {
        params: {
          include_calculated_balance: true,  // Use calculated balance, not stored account_balance
          limit: 2000,
        }
      });
      const sortedCustomers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setCustomers(sortedCustomers);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  // Server-side search with live balances when user types 2+ chars
  useEffect(() => {
    if (searchQuery.trim().length < 2) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/customers/search`, {
          params: { q: searchQuery.trim(), include_calculated_balance: true }
        });
        setCustomers(response.data || []);
      } catch (error) {
        console.error("Error searching customers:", error);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredCustomers = searchQuery.trim().length >= 2
    ? customers  // server already filtered with live balance
    : customers.filter(c =>
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.account_number?.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const handleSelectCustomer = async (customer) => {
    // Show customer immediately (with static balance) while fetching live balance
    setSelectedCustomer(customer);
    const initialBalance = customer.calculated_balance ?? customer.account_balance ?? 0;
    if (initialBalance > 0) {
      setPaymentAmount(initialBalance.toFixed(2));
    } else {
      setPaymentAmount("");
    }

    // Fetch live calculated balance from the individual customer endpoint
    setFetchingBalance(true);
    try {
      const response = await axios.get(`${API}/customers/${customer.id}`);
      if (response.data) {
        setSelectedCustomer(response.data);
        const liveBalance = response.data.calculated_balance ?? response.data.account_balance ?? 0;
        if (liveBalance > 0) {
          setPaymentAmount(liveBalance.toFixed(2));
        } else {
          setPaymentAmount("");
        }
      }
    } catch (error) {
      console.error("Error fetching live customer balance:", error);
    } finally {
      setFetchingBalance(false);
    }
  };

  const handleAddAccountPayment = () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer");
      return;
    }

    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const balance = selectedCustomer.calculated_balance ?? selectedCustomer.account_balance ?? 0;
    if (amount > balance) {
      toast.error(`Amount exceeds balance owing (R ${balance.toFixed(2)})`);
      return;
    }

    // Create an account payment item for the cart
    const accountPaymentItem = {
      product_id: `ACCOUNT-PAYMENT-${selectedCustomer.id}`,
      plu_code: "ACCOUNT-PAYMENT",
      name: `Account Payment - ${selectedCustomer.name}`,
      quantity: 1,
      unit_price: amount,
      discount_percent: 0,
      max_discount_percent: 0,
      no_discount: true,
      sold_per_kg: false,
      total: amount,
      max_stock: 999999,
      vat_category: "Zero-rated 0%",
      image_url: null,
      is_account_payment: true,
      customer_id: selectedCustomer.id,
      customer_name: selectedCustomer.name,
      customer_account_number: selectedCustomer.account_number
    };

    onAddToCart(accountPaymentItem);
    toast.success(`Account payment of R ${amount.toFixed(2)} added to cart for ${selectedCustomer.name}`);
    onClose();
  };

  const getCustomerBalance = (customer) => {
    return customer.calculated_balance ?? customer.account_balance ?? 0;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden" data-testid="customer-balance-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Wallet className="h-6 w-6 text-blue-600" />
            Check Customer Balance
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by customer name or account number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-lg"
              autoFocus
              data-testid="customer-balance-search"
            />
          </div>

          {/* Customer List / Selected Customer */}
          {!selectedCustomer ? (
            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No customers found
                </div>
              ) : (
                <div className="divide-y">
                  {filteredCustomers.map((customer) => {
                    const balance = getCustomerBalance(customer);
                    return (
                      <div
                        key={customer.id}
                        onClick={() => handleSelectCustomer(customer)}
                        className="p-4 cursor-pointer hover:bg-blue-50 transition-colors flex justify-between items-center"
                        data-testid={`customer-balance-option-${customer.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-semibold">{customer.name}</div>
                            <div className="text-sm text-gray-500">
                              {customer.account_number} {customer.contact_number && `• ${customer.contact_number}`}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`font-bold text-lg ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            R {balance.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {balance > 0 ? 'Owing' : balance < 0 ? 'Credit' : 'Settled'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Selected Customer View */
            <div className="space-y-4">
              {/* Customer Info Card */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-bold text-lg">{selectedCustomer.name}</div>
                      <div className="text-sm text-gray-600">
                        {selectedCustomer.account_number}
                        {selectedCustomer.contact_number && ` • ${selectedCustomer.contact_number}`}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedCustomer(null)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Change
                  </Button>
                </div>
              </div>

              {/* Balance Display */}
              <div className={`p-6 rounded-lg border-2 text-center ${
                getCustomerBalance(selectedCustomer) > 0 
                  ? 'bg-red-50 border-red-300' 
                  : 'bg-green-50 border-green-300'
              }`}>
                <div className="text-sm font-medium text-gray-600 mb-1">Account Balance</div>
                {fetchingBalance ? (
                  <div className="flex items-center justify-center gap-2 py-2">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    <span className="text-gray-500 text-sm">Fetching live balance...</span>
                  </div>
                ) : (
                  <>
                    <div className={`text-4xl font-bold ${
                      getCustomerBalance(selectedCustomer) > 0 ? 'text-red-600' : 'text-green-600'
                    }`} data-testid="customer-balance-amount">
                      R {getCustomerBalance(selectedCustomer).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {getCustomerBalance(selectedCustomer) > 0 
                        ? 'Amount owing' 
                        : getCustomerBalance(selectedCustomer) < 0 
                          ? 'Credit balance' 
                          : 'Account settled'}
                    </div>
                  </>
                )}
              </div>

              {/* Payment Input - Only show if balance owing */}
              {getCustomerBalance(selectedCustomer) > 0 && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                  <Label className="text-sm font-semibold">Amount to Pay Off</Label>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Input
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        placeholder="0.00"
                        className="h-12 text-xl text-center font-bold"
                        step="0.01"
                        min="0.01"
                        max={getCustomerBalance(selectedCustomer)}
                        data-testid="account-payment-amount"
                      />
                    </div>
                    <Button
                      variant="outline"
                      className="h-12 px-4 border-blue-300 text-blue-700 hover:bg-blue-50"
                      onClick={() => setPaymentAmount(getCustomerBalance(selectedCustomer).toFixed(2))}
                    >
                      Full Amount
                    </Button>
                  </div>

                  {parseFloat(paymentAmount) > getCustomerBalance(selectedCustomer) && (
                    <div className="flex items-center gap-2 text-amber-600 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      Amount exceeds balance owing
                    </div>
                  )}
                </div>
              )}

              {/* No balance message */}
              {getCustomerBalance(selectedCustomer) <= 0 && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200 text-center">
                  <div className="text-green-700 font-medium">
                    {getCustomerBalance(selectedCustomer) === 0 
                      ? 'This account is fully settled - no payment needed'
                      : 'This account has a credit balance - no payment needed'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="customer-balance-cancel">
            Cancel
          </Button>
          {selectedCustomer && getCustomerBalance(selectedCustomer) > 0 && (
            <Button
              onClick={handleAddAccountPayment}
              disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || parseFloat(paymentAmount) > getCustomerBalance(selectedCustomer)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="add-account-payment-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add to Cart
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerBalanceModal;
